import {
  ADJUSTER_LICENSE_STATES,
  ROLES,
  TEMPLATE_MODULES,
  buildTemplateKey,
} from '@components/AdminPanelComponent/Users/constants';

const ROLES_BY_LOWERCASE = Object.values(ROLES).reduce((acc, role) => {
  acc[role.toLowerCase()] = role;
  return acc;
}, {});

const DEFAULT_USER = {
  id: null,
  firstName: '',
  lastName: '',
  middleName: '',
  email: '',
  username: '',
  title: '',
  phone: '',
  orgId: null,
  isActive: false,
  isBlocked: false,
  isTempUser: false,
  isSuperAdmin: false,
  signupStatus: '',
  userRole: '',
  jobRole: '',
  eeCode: '',
  supervisorId: null,
  createdBy: null,
  updatedBy: null,
  coordinate: '',
  exzeoId: '',
  isEmailVerified: false,
  timezone: '',
  openWorkkardCount: 0,
  closeWorkkardCount: 0,
  openActivitiesCount: 0,
  closeActivitiesCount: 0,
  images: null,
  permissions: null,
  licenses: []
};

export const toOrgId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const normalizeUser = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_USER };
  return {
    id:                  raw.id ?? raw.user_id ?? DEFAULT_USER.id,
    firstName:           raw.first_name ?? DEFAULT_USER.firstName,
    lastName:            raw.last_name ?? DEFAULT_USER.lastName,
    middleName:          raw.middle_name ?? DEFAULT_USER.middleName,
    email:               raw.email ?? DEFAULT_USER.email,
    username:            raw.user_name ?? raw.username ?? DEFAULT_USER.username,
    title:               raw.title ?? raw.job_title ?? DEFAULT_USER.title,
    phone:               raw.phone ?? DEFAULT_USER.phone,
    orgId:               toOrgId(raw.org_id ?? raw.OrgId ?? DEFAULT_USER.orgId),
    isActive:            raw.is_deleted != null ? !raw.is_deleted : (raw.is_active ?? DEFAULT_USER.isActive),
    isBlocked:           raw.is_deleted != null ? !!raw.is_deleted : (raw.blocked ?? (raw.is_active != null ? !raw.is_active : DEFAULT_USER.isBlocked)),
    isTempUser:          raw.is_temp_user ?? raw.temp_user ?? DEFAULT_USER.isTempUser,
    isSuperAdmin:        raw.is_super_admin ?? DEFAULT_USER.isSuperAdmin,
    signupStatus:        raw.signup_status ?? DEFAULT_USER.signupStatus,
    userRole:            raw.user_role ?? DEFAULT_USER.userRole,
    jobRole:             raw.job_role ?? DEFAULT_USER.jobRole,
    eeCode:              raw.ee_code ?? DEFAULT_USER.eeCode,
    supervisorId:        raw.supervisor_id ?? DEFAULT_USER.supervisorId,
    createdBy:           raw.created_by ?? DEFAULT_USER.createdBy,
    updatedBy:           raw.updated_by ?? DEFAULT_USER.updatedBy,
    coordinate:          raw.coordinate ?? DEFAULT_USER.coordinate,
    exzeoId:             raw.exzeo_id ?? DEFAULT_USER.exzeoId,
    isEmailVerified:     raw.is_email_verified ?? DEFAULT_USER.isEmailVerified,
    timezone:            raw.timezone ?? DEFAULT_USER.timezone,
    openWorkkardCount:   raw.OpenWorkkardCount ?? raw.open_workkard_count ?? DEFAULT_USER.openWorkkardCount,
    closeWorkkardCount:  raw.ClosedWorkkardCount ?? raw.closed_workkard_count ?? DEFAULT_USER.closeWorkkardCount,
    openActivitiesCount: raw.OpenActivityCount ?? raw.open_activity_count ?? DEFAULT_USER.openActivitiesCount,
    closeActivitiesCount:raw.ClosedActivityCount ?? raw.closed_activity_count ?? DEFAULT_USER.closeActivitiesCount,
    images:              raw.images ?? DEFAULT_USER.images,
    permissions:         raw.permissions ?? DEFAULT_USER.permissions,
    licenses:            raw.licenses ?? DEFAULT_USER.licenses,
    profileImage:        raw.profile_image,
  };
};

export const normalizeUserListResponse = (response = {}) => {
  const rawUsers = response?.users ?? response?.Users ?? response?.data?.users ?? [];
  const users = Array.isArray(rawUsers) ? rawUsers.map(normalizeUser) : [];
  const count = response?.count ?? response?.Count ?? response?.totalCount ?? users.length;

  return { users, count };
};

export const normalizeSupervisorOption = (raw = {}) => ({
  value: raw.id ?? raw.user_id ?? raw.UserId ?? null,
  label: `${raw.first_name ?? raw.FirstName ?? ''} ${raw.last_name ?? raw.LastName ?? ''}`.trim() || 'Unknown',
});

export const normalizeSupervisorsResponse = (response = {}) => {
  const rawUsers = response?.users ?? response?.Users ?? [];
  return Array.isArray(rawUsers) ? rawUsers.map(normalizeSupervisorOption) : [];
};

export const normalizeTemplatesResponse = (response = {}) => {
  const rawTemplates = Array.isArray(response)
    ? response
    : response?.templates?.data || response?.data || [];

  if (!Array.isArray(rawTemplates)) return [];

  return rawTemplates.map((t) => {
    const rawModule = t.module ?? t.type ?? t.Type ?? '';
    return {
      id: t.id ?? t.template_id ?? t.TemplateId,
      name: t.name ?? t.template_name ?? t.TemplateName ?? 'Unknown',
      short_name: t.template_short_name ?? t.short_name ?? t.shortName ?? t.ShortName ?? '',
      module: rawModule ? (rawModule.charAt(0).toUpperCase() + rawModule.slice(1)) : '',
      isActive: t.is_active ?? true,
    };
  });
};

export const PERMISSION_KEY_MAP = {
  CanReAssignUser: 'can_re_assign_a_user',
  CanEditUser: 'can_edit_user',
  CanBlockUser: 'can_block_a_user',
  CanAddUser: 'can_add_user',
  CanSendB2BRelationshipRequest: 'can_send_b2b_relationship_request',
  CanAcceptRejectB2BRelationshipRequest: 'can_accept_reject_b2b_relationship_request',
  CanManageContracts: 'can_manage_contracts',
  CanDeAssociateB2BRelationship: 'can_de_associate_b2b_relationship',
  CanAddRemoveOrganizationfromExcludelist: 'can_add_remove_organization_from_exclude_list',
  CanCreateWorkkard: 'can_create_workkard',
  CanChangeWorkkardSponsor: 'can_change_workkard_sponsor',
  CanArchiveWorkkard: 'can_archive_workkard',
  CanReceiveUnAssignedWorkkards: 'can_receive_un_assigned_workkards',
  CanCloseWorkkard: 'can_close_workkard',
  CanEditWorkkard: 'can_edit_workkard',
  CanEditClosedWorkkard: 'can_edit_closed_workkard',
  CanReopenWorkkard: 'can_reopen_workkard',
  CanCreateActivity: 'can_create_activity',
  CanReassignActivity: 'can_reassign_activity',
  CanAcceptRejectActivity: 'can_accept_reject_activity',
  CanArchiveActivity: 'can_archive_activity',
  CanCloseActivity: 'can_close_activity',
  CanAcceptRejectBid: 'can_accept_reject_bid',
  CanReceiveUnAssignedActivities: 'can_receive_un_assigned_activities',
  CanReopenActivity: 'can_reopen_activity',
  CanChangeActivityOwner: 'can_change_activity',
  CanMarkActivityAsInProgress: 'can_mark_activity_as_in_progress',
  CanCompleteActivity: 'can_complete_activity',
  CanCloseActivityduringMidterm: 'can_close_activity_during_mid_term',
  CanAssignB2B: 'can_assign_b2b',
  CanEditActivity: 'can_edit_activity',
  CanAddPersonalNotes: 'can_add_personal_notes',
  CanAddPrivateNotes: 'can_add_private_notes',
  CanAddPublicNotes: 'can_add_public_notes',
  CanDeleteUpdateNotesWithinConfiguredTimeFrame: 'can_delete_update_notes_within_configured_time_frame',
  CanCloneNotes: 'can_clone_notes',
  CanAddPublicNotesWorkkard: 'can_add_public_notes_workkard',
  CanAddInternalNotesWorkkard: 'can_add_private_notes_workkard',
  CanAddPersonalNotesWorkkard: 'can_add_personal_notes_workkard',
  CanAddPublicNotesActivity: 'can_add_public_notes_activity',
  CanAddInternalNotesActivity: 'can_add_private_notes_activity',
  CanAddPersonalNotesActivity: 'can_add_personal_notes_activity',
  CanManageSettings: 'can_manage_settings',
  CanManageCompanyProfile: 'can_manage_company_profile',
  CanResetPassword: 'can_reset_password',
  CanAddWatchers: 'can_add_watcher',
  CanActAsHiddenWatcher: 'can_act_as_hidden_watcher',
  CanViewWorkload: 'can_view_workload',
  CanViewB2BWorkload: 'can_view_b2b_workload',
  CanAddMatrixReport: 'can_add_matrix_report',
  InviteUserToAccessCommunity: 'invite_people_to_access_community',
  CanAddCustomFields: 'can_add_custom_fields',
  CanAddNoteTemplates: 'can_add_note_templates',
  CanManageRules: 'can_manage_rules',
  CanCloseActivityInViews: 'can_close_act_in_views',
  CanCompleteDiaryInViews: 'can_complete_diary_in_views',
  CanCompleteOrReopenDiary: 'can_completeorreopen_diary',
  CanDeleteTodo: 'can_delete_todo',
  CanEditTodo: 'can_edit_todo',
  CanViewDeletedTodo: 'can_view_deleted_todo',
  CanCreateParty: 'can_create_party',
  CanDeleteParty: 'can_delete_party',
  CanConnectMailbot: 'can_connect_mailbot',
  CanAccessFinancials: 'can_access_financials',
  CanAccessInbox: 'can_access_inbox',
  CanManageInbox: 'can_manage_inbox',
  CanAccessAccounts: 'can_access_accounts',
  CanAccessLetters: 'can_access_letters',
  CanRequestForLetter: 'can_request_for_letter',
  CanAccessMercury: 'can_access_mercury',
  CanManageAtlas: 'can_manage_atlas',
  CanApplyAggregationInViews: 'can_apply_aggregation_in_views',
  CanManageCommunication: 'can_manage_communication',
  CanAccessBulkInbox: 'can_access_bulk_inbox',
  CanAccessReviewSystem: 'can_access_review_system',
};

export const normalizePermissionsResponse = (permissionset = {}) => {
  const normalized = {};

  Object.entries(permissionset).forEach(([key, value]) => {

    if (typeof value === 'boolean') {
      const mappedKey = PERMISSION_KEY_MAP[key] || key;
      normalized[mappedKey] = value;
      return;
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);

        const permissionLists = Array.isArray(parsed)
          ? parsed.map((section) => section.permissions).filter(Array.isArray)
          : (parsed.permissions && Array.isArray(parsed.permissions) ? [parsed.permissions] : []);

        permissionLists.forEach((permissions) => {
          permissions.forEach((perm) => {
            normalized[perm.key] = !!perm.is_checked;

            if (perm.options) {
              perm.options.forEach((opt) => {
                normalized[opt.key] = !!opt.is_checked;
              });
            }
          });
        });

        if (parsed.states) {
          parsed.states.forEach((s) => {
            normalized[`state_${s.state_code}`] = !!s.is_checked;
          });
        }

        if (parsed.access_letters) {
          if (parsed.key) {
            normalized[parsed.key] = !!parsed.is_checked;
          }
          if (parsed.can_request !== undefined) {
            normalized.can_request_for_letter = !!parsed.can_request;
          }
          parsed.access_letters.forEach((item) => {
            normalized[item.key] = !!item.is_checked;

            if (item.options) {
              item.options.forEach((opt) => {
                normalized[opt.key] = !!opt.is_checked;
              });
            }
          });
        }

        if (parsed.manage_letters) {
          if (parsed.key) {
            normalized[parsed.key] = !!parsed.is_checked;
          }
          parsed.manage_letters.forEach((item) => {
            normalized[item.key] = !!item.is_checked;
          });
        }

      } catch (e) {
      }
    }
  });

  return normalized;
};

export const normalizeUserDetailResponse = (response = {}) => {
  if (!response || typeof response !== 'object') return {};

  const um = response.user ?? response.um ?? {};
  const permissionset = response.permissions ?? response.permissionset ?? {};
  const rawTemplates = response.templates?.data ?? response.templates ?? [];
  const tmplIds = response.tmplIds ?? [];
  const licenses = response.licenses?.data ?? response.licenses ?? um.LicenseInfo ?? [];

  const formValues = {};
  formValues.id = um.Id ?? um.id ?? um.UserId ?? null;

  formValues.first_name = um.FirstName ?? um.first_name ?? '';
  formValues.last_name = um.LastName ?? um.last_name ?? '';
  formValues.middle_name = um.MiddleName ?? um.middle_name ?? '';
  formValues.email = um.Email ?? um.email ?? '';
  formValues.username = um.UserName ?? um.user_name ?? '';
  formValues.phone = um.Telephone ?? um.Phone ?? um.phone ?? '';
  formValues.job_title = um.Title ?? um.JobTitle ?? um.title ?? um.job_title ?? '';
  const rawJobRole = um.JobRole ?? um.job_role ?? '';
  if (Array.isArray(rawJobRole)) {
    formValues.job_role = rawJobRole;
  } else if (typeof rawJobRole === 'string' && rawJobRole.length > 0) {
    formValues.job_role = rawJobRole.split(',').map((s) => s.trim()).filter(Boolean);
  } else {
    formValues.job_role = [];
  }
  formValues.ee_code = um.EECode ?? um.ee_code ?? '';
  formValues.supervisor_id = um.SupervisorId ?? um.supervisor_id ?? null;
  formValues.temp_user = um.IsTempUser ?? um.TempUser ?? um.temp_user ?? um.is_temp_user ?? false;
  const rawRole = um.UserRole ?? um.user_role ?? '';
  formValues.role = ROLES_BY_LOWERCASE[rawRole.toLowerCase()] ?? ROLES.UNSURE;

  const licenseList = Array.isArray(licenses) ? licenses : [];
  if (licenseList.length > 0) {
    const authorizedStateCodes = [];
    licenseList.forEach(license => {
      const stateCode = license.State ?? license.state;
      const licenseNumber = license.LicenseNumber ?? license.license_number;
      const expirationIso = license.expiration_date_iso ?? license.ExpirationDateISO;
      const expirationRaw = license.ExpirationDate ?? license.expiration_date;
      if (stateCode && licenseNumber) {
        formValues[`license_number_${stateCode}`] = licenseNumber;
      }
      if (stateCode) {
        authorizedStateCodes.push(stateCode);
        let dateValue = null;
        if (expirationIso) {
          dateValue = new Date(expirationIso);
        } else if (expirationRaw != null) {
          // Handle unix timestamp in seconds vs milliseconds vs ISO string
          if (typeof expirationRaw === 'number') {
            const ms = expirationRaw < 1e12 ? expirationRaw * 1000 : expirationRaw;
            dateValue = new Date(ms);
          } else {
            dateValue = new Date(expirationRaw);
          }
        }
        if (dateValue && !isNaN(dateValue.getTime())) {
          formValues[`license_expiration_${stateCode}`] = dateValue.toISOString().split('T')[0];
        }
      }
    });
    if (authorizedStateCodes.length > 0) {
      formValues.adjuster_license_states = authorizedStateCodes;
    }
  }

  const normalizedPermissions = normalizePermissionsResponse(permissionset);

  Object.entries(normalizedPermissions).forEach(([key, value]) => {
    formValues[key] = value;
  });

  const markTemplate = (id, module) => {
    (module ? [module] : TEMPLATE_MODULES).forEach((m) => {
      formValues[buildTemplateKey(m, id)] = true;
    });
  };

  if (Array.isArray(rawTemplates) && rawTemplates.length > 0) {
    rawTemplates.forEach((t) => {
      const isObject = typeof t === 'object';
      const id = isObject ? (t.template_id ?? t.id) : t;
      if (id != null) {
        markTemplate(id, isObject ? (t.module ?? t.type ?? t.Type) : null);
      }
    });
  } else if (Array.isArray(tmplIds) && tmplIds.length > 0) {
    tmplIds.forEach((id) => markTemplate(id, null));
  }

  return formValues;
};

export const normalizeUserProfileResponse = (response = {}) => {
  if (!response || typeof response !== 'object') return null;

  const um = response.user ?? response.um ?? {};
  const licenses = response.licenses?.data ?? response.licenses ?? um.LicenseInfo ?? [];

  const firstName = um.FirstName ?? um.first_name ?? '';
  const lastName = um.LastName ?? um.last_name ?? '';
  const rawJobRole = um.JobRole ?? um.job_role ?? '';
  const jobRole = Array.isArray(rawJobRole)
    ? rawJobRole
    : (typeof rawJobRole === 'string' && rawJobRole.length > 0
        ? rawJobRole.split(',').map((s) => s.trim()).filter(Boolean)
        : []);

  const licenseList = (Array.isArray(licenses) ? licenses : []).map((license) => {
    const expirationIso = license.expiration_date_iso ?? license.ExpirationDateISO;
    const expirationRaw = license.ExpirationDate ?? license.expiration_date;
    let expirationDate = null;
    if (expirationIso) {
      expirationDate = new Date(expirationIso);
    } else if (expirationRaw != null) {
      const ms = typeof expirationRaw === 'number'
        ? (expirationRaw < 1e12 ? expirationRaw * 1000 : expirationRaw)
        : expirationRaw;
      expirationDate = new Date(ms);
    }

    return {
      state: license.State ?? license.state ?? '',
      licenseNumber: license.LicenseNumber ?? license.license_number ?? '',
      expirationDate: expirationDate && !isNaN(expirationDate.getTime())
        ? expirationDate.toLocaleDateString()
        : '',
    };
  });

  return {
    id: um.Id ?? um.id ?? null,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim() || 'Unknown User',
    email: um.Email ?? um.email ?? '',
    phone: um.Telephone ?? um.Phone ?? um.phone ?? '',
    jobTitle: um.Title ?? um.JobTitle ?? um.title ?? um.job_title ?? '',
    jobRole,
    supervisorId: um.SupervisorId ?? um.supervisor_id ?? null,
    supervisorName: um.SupervisorName ?? um.supervisor_name ?? null,
    orgId: toOrgId(um.OrgId ?? um.org_id),
    licenses: licenseList,
  };
};

export const normalizeCurrentUser = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  const userData = raw.user ?? raw;
  const normalized = normalizeUser(userData);

  const rawPerms = raw.permissions ?? userData.permissions ?? {};
  
  const permissions = { ...rawPerms };

  if (typeof rawPerms.mercury_permissions === 'string') {
    try {
      const parsed = JSON.parse(rawPerms.mercury_permissions);
      permissions.mercury = {
        can_access: rawPerms.can_access_mercury ?? false,
        display_name: parsed.name || 'Mercury Permissions',
        permissions: parsed.permissions || [],
      };
    } catch (e) {}
  }

  if (typeof rawPerms.inbox_permissions === 'string') {
    try {
      const parsed = JSON.parse(rawPerms.inbox_permissions);
      permissions.inbox = {
        can_access: rawPerms.can_access_inbox ?? false,
        display_name: parsed.name || 'Inbox Permissions',
        permissions: parsed.permissions || [],
      };
    } catch (e) {}
  }

  if (typeof rawPerms.financial_permissions === 'string') {
    try {
      const parsed = JSON.parse(rawPerms.financial_permissions);
      permissions.financials = {
        can_access: rawPerms.can_access_financials ?? false,
        display_name: 'Can Access Financials',
        permissions: Array.isArray(parsed) ? parsed : [],
      };
    } catch (e) {}
  }

  if (typeof rawPerms.authorized_states === 'string') {
    try {
      const parsed = JSON.parse(rawPerms.authorized_states);
      permissions.authorized_states = {
        Data: {
          name: parsed.name || 'Authorized States',
          states: parsed.states || [],
        },
      };
    } catch (e) {}
  }

  if (typeof rawPerms.review_system === 'string') {
    try {
      const parsed = JSON.parse(rawPerms.review_system);
      permissions.review_system = {
        display_name: parsed.name || 'Review System',
        permissions: parsed.permissions || [],
      };
    } catch (e) {}
  }

  const hasLetterAdmin = typeof rawPerms.letter_admin_permissions === 'string';
  const hasLetterWeb = typeof rawPerms.letter_web_permissions === 'string';
  if (hasLetterAdmin || hasLetterWeb) {
    try {
      const letters = {
        display_name: 'Letters',
        can_access: rawPerms.can_access_letters ?? false,
        can_request: rawPerms.can_request_for_letter ?? false,
      };

      if (hasLetterAdmin) {
        const adminParsed = JSON.parse(rawPerms.letter_admin_permissions);
        letters.admin_permissions = adminParsed.manage_letters || [];
      }

      if (hasLetterWeb) {
        const webParsed = JSON.parse(rawPerms.letter_web_permissions);
        letters.user_permissions = webParsed.access_letters || [];
      }

      permissions.letters = letters;
    } catch (e) {}
  }

  normalized.permissions = permissions;

  normalized.canBlockUser = rawPerms.can_block_a_user ?? false;
  normalized.canEditUser = rawPerms.can_edit_user ?? false;
  normalized.canResetPassword = rawPerms.can_reset_password ?? false;
  normalized.canAddUser = rawPerms.can_add_user ?? false;
  normalized.canReassignUser = rawPerms.can_re_assign_a_user ?? false;
  normalized.canViewWorkload = rawPerms.can_view_workload ?? false;

  const rawTemplates = raw.templates?.data ?? raw.templates ?? [];
  if (Array.isArray(rawTemplates)) {
    normalized.templates = rawTemplates;
  }

  return normalized;
};

export const isSuccessResponse = (response) => {
  if (!response) return false;
  if (response.error || response.Error) return false;
  if (response.success === false) return false;
  if (typeof response === 'object') return true;
  return false;
};

export const getErrorMessage = (responseOrError, fallback = 'An error occurred') => {
  if (!responseOrError) return fallback;
  if (typeof responseOrError === 'string') return responseOrError;
  return (
    responseOrError.message ??
    responseOrError.msg ??
    responseOrError.error ??
    responseOrError.Error ??
    fallback
  );
};
const BE_FIELD_NAME_TO_FORM_FIELD = {
  Email: 'email',
  UserName: 'username',
  Username: 'username',
  FirstName: 'first_name',
  LastName: 'last_name',
  MiddleName: 'middle_name',
  Phone: 'phone',
  Telephone: 'phone',
  JobTitle: 'job_title',
  Title: 'job_title',
  EECode: 'ee_code',
  SupervisorId: 'supervisor_id',
  Role: 'role',
  UserRole: 'role',
  JobRole: 'job_role',
  OrgId: 'org_id',
};

const fallbackFieldNameToFormField = (name) =>
  String(name)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();

const FIELD_NAME_KEYS = ['fieldName', 'field_name', 'field', 'Field', 'FieldName'];
const FIELD_MESSAGE_KEYS = ['errorMsg', 'error_message', 'message', 'Message', 'ErrorMsg', 'error'];

const readFirstDefined = (obj, keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj[key] !== '') return obj[key];
  }
  return undefined;
};
const extractFieldErrors = (parsed) => {
  const fieldErrors = {};
  if (!parsed || typeof parsed !== 'object') return fieldErrors;

  const list = parsed.fields ?? parsed.Fields ?? parsed.errors ?? parsed.Errors;
  if (!Array.isArray(list)) return fieldErrors;

  list.forEach((f) => {
    const fieldName = readFirstDefined(f, FIELD_NAME_KEYS);
    const errorMsg = readFirstDefined(f, FIELD_MESSAGE_KEYS);
    if (!fieldName || !errorMsg) return;
    const formField =
      BE_FIELD_NAME_TO_FORM_FIELD[fieldName] ?? fallbackFieldNameToFormField(fieldName);
    fieldErrors[formField] = errorMsg;
  });

  return fieldErrors;
};
const extractBalancedJson = (str) => {
  const start = str.indexOf('{');
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < str.length; i++) {
    const ch = str[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
    } else if (ch === '{') {
      depth += 1;
    } else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        return str.slice(start, i + 1);
      }
    }
  }

  return null;
};
export const parseUserApiError = (responseOrError, fallback = 'An error occurred') => {
  const rawMessage = getErrorMessage(responseOrError, fallback);
  const directCandidates = [
    responseOrError,
    responseOrError?.data,
    responseOrError?.error,
    responseOrError?.response?.data,
  ].filter((c) => c && typeof c === 'object');

  for (const candidate of directCandidates) {
    const fieldErrors = extractFieldErrors(candidate);
    if (Object.keys(fieldErrors).length > 0) {
      return {
        message: readFirstDefined(candidate, ['message', 'Message']) ?? rawMessage,
        fieldErrors,
      };
    }
  }
  if (typeof rawMessage === 'string') {
    const jsonSlice = extractBalancedJson(rawMessage);
    if (jsonSlice) {
      try {
        const parsed = JSON.parse(jsonSlice);
        const fieldErrors = extractFieldErrors(parsed);
        return {
          message: readFirstDefined(parsed, ['message', 'Message']) ?? rawMessage,
          fieldErrors,
        };
      } catch (e) {
      }
    }
  }
  return { message: rawMessage, fieldErrors: {} };
};

export const enrichUser = (user, loggedInUserId) => {
  const isSelf = loggedInUserId != null && String(user.id) === String(loggedInUserId);

  return {
    ...user,
    isSelf,
    canBeBlocked:       !user.isBlocked && !isSelf,
    canBeUnblocked:     user.isBlocked && !isSelf,
    canResetPassword:   !isSelf,
    displayName:        `${user.firstName} ${user.lastName}`.trim() || 'Unknown User',
    initials:           getInitials(user.firstName, user.lastName),
    avatarColor:        getAvatarColor(`${user.firstName}${user.lastName}`),
  };
};

const extractLicenseData = (formValues, authorizedStates = []) => {
  const licenses = [];
  
  if (!authorizedStates || authorizedStates.length === 0) {
    return licenses;
  }

  authorizedStates.forEach(state => {
    const stateCode = state.state_code;
    const licenseNumberKey = `license_number_${stateCode}`;
    const licenseExpirationKey = `license_expiration_${stateCode}`;

    const licenseNumber = formValues[licenseNumberKey];
    const expirationDate = formValues[licenseExpirationKey];

    if (licenseNumber && expirationDate) {
      const expirationTimestamp = Math.floor(new Date(expirationDate).getTime() / 1000);
      const expirationISO = new Date(expirationDate).toISOString();

      licenses.push({
        state: stateCode,
        license_number: licenseNumber,
        expiration_date: expirationTimestamp,
        expiration_date_iso: expirationISO,
      });
    }
  });

  return licenses;
};

const extractMercuryPermissions = (formValues, currentUser) => {
  if (!currentUser?.permissions?.mercury?.permissions) {
    return null;
  }

  const mercuryPermissions = currentUser.permissions.mercury.permissions.map(perm => ({
    name: perm.name,
    key: perm.key,
    is_checked: !!formValues[perm.key],
  }));

  return {
    name: currentUser.permissions.mercury.display_name || 'Mercury Permissions',
    permissions: mercuryPermissions,
  };
};

const extractInboxPermissions = (formValues, currentUser) => {
  if (!currentUser?.permissions?.inbox?.permissions) {
    return null;
  }

  const inboxPermissions = currentUser.permissions.inbox.permissions.map(perm => ({
    name: perm.name,
    key: perm.key,
    is_checked: !!formValues[perm.key],
  }));

  return {
    name: currentUser.permissions.inbox.display_name || 'Inbox Permissions',
    permissions: inboxPermissions,
  };
};

const extractReviewSystemPermissions = (formValues, currentUser) => {
  if (!currentUser?.permissions?.review_system?.permissions) {
    return null;
  }

  const reviewPermissions = currentUser.permissions.review_system.permissions.map(perm => ({
    name: perm.name,
    key: perm.key,
    is_checked: !!formValues[perm.key],
  }));

  return {
    name: currentUser.permissions.review_system.display_name || 'Review System',
    permissions: reviewPermissions,
  };
};

const extractFinancialPermissions = (formValues, currentUser) => {
  if (!currentUser?.permissions?.financials?.permissions) {
    return null;
  }

  const financialSections = currentUser.permissions.financials.permissions.map(section => {
    const sectionPermissions = (section?.permissions || []).map(perm => {
      const permObj = {
        name: perm.name,
        key: perm.key,
        is_checked: !!formValues[perm.key],
      };

      if (perm.options && perm.options.length > 0) {
        permObj.options = perm.options.map(opt => ({
          name: opt.name,
          key: opt.key,
          is_checked: !!formValues[opt.key],
        }));
      }

      return permObj;
    });

    return {
      section_name: section?.section_name,
      permissions: sectionPermissions,
    };
  });

  return financialSections;
};

const extractAuthorizedStates = (formValues, currentUser) => {
  if (!currentUser?.permissions?.authorized_states?.Data?.states) {
    return null;
  }

  const states = currentUser.permissions.authorized_states.Data.states.map(state => ({
    state_code: state.state_code,
    state_name: state.state_name,
    is_checked: !!formValues[`state_${state.state_code}`],
  }));

  return {
    name: 'Authorized States',
    states: states,
  };
};

const extractLetterWebPermissions = (formValues, currentUser) => {
  if (!currentUser?.permissions?.letters?.user_permissions?.length) {
    return null;
  }

  const accessLetters = currentUser.permissions.letters.user_permissions.map(perm => {
    const permObj = {
      name: perm.name,
      key: perm.key,
      is_checked: !!formValues[perm.key],
    };

    if (perm.options && perm.options.length > 0) {
      permObj.options = perm.options.map(opt => ({
        name: opt.name,
        key: opt.key,
        is_checked: !!formValues[opt.key],
      }));
    }

    return permObj;
  });
  const webData = {
    name: 'Can Access Letters',
    key: 'can_access_letters',
    is_checked: !!(formValues.can_access_letters ?? formValues.can_manage_letters),
    access_letters: accessLetters,
  };

  return webData;
};

const extractLetterAdminPermissions = (formValues, currentUser) => {
  if (!currentUser?.permissions?.letters?.admin_permissions?.length) {
    return null;
  }

  const manageLetters = currentUser.permissions.letters.admin_permissions.map(perm => ({
    name: perm.name,
    key: perm.key,
    is_checked: !!formValues[perm.key],
  }));

  return {
    name: 'Can Manage Letters',
    key: 'can_manage_letters',
    is_checked: !!formValues.can_manage_letters,
    manage_letters: manageLetters,
  };
};

const extractDynamicPermissions = (formValues, currentUser) => {
  const dynamicPermissions = {};

  const mercuryData = extractMercuryPermissions(formValues, currentUser);
  if (mercuryData) {
    dynamicPermissions.mercury_permissions = JSON.stringify(mercuryData);
  }

  const inboxData = extractInboxPermissions(formValues, currentUser);
  if (inboxData) {
    dynamicPermissions.inbox_permissions = JSON.stringify(inboxData);
  }

  const financialData = extractFinancialPermissions(formValues, currentUser);
  if (financialData) {
    dynamicPermissions.financial_permissions = JSON.stringify(financialData);
  }

  const authorizedStatesData = extractAuthorizedStates(formValues, currentUser);
  if (authorizedStatesData) {
    dynamicPermissions.authorized_states = JSON.stringify(authorizedStatesData);
  }

  const letterWebData = extractLetterWebPermissions(formValues, currentUser);
  if (letterWebData) {
    dynamicPermissions.letter_web_permissions = JSON.stringify(letterWebData);
  }

  const letterAdminData = extractLetterAdminPermissions(formValues, currentUser);
  if (letterAdminData) {
    dynamicPermissions.letter_admin_permissions = JSON.stringify(letterAdminData);
  }

  const reviewSystemData = extractReviewSystemPermissions(formValues, currentUser);
  if (reviewSystemData) {
    dynamicPermissions.review_system = JSON.stringify(reviewSystemData);
  }

  return dynamicPermissions;
};

const isDynamicPermissionKey = (key, currentUser) => {
  const alwaysExclude = ['authorized_states', 'can_manage_letters', 'can_access_letters'];
  if (alwaysExclude.includes(key)) return true;

  if (currentUser?.permissions?.mercury?.permissions) {
    const mercuryKeys = currentUser.permissions.mercury.permissions.map(p => p.key);
    if (mercuryKeys.includes(key)) return true;
  }

  if (currentUser?.permissions?.inbox?.permissions) {
    const inboxKeys = currentUser.permissions.inbox.permissions.map(p => p.key);
    if (inboxKeys.includes(key)) return true;
  }

  if (currentUser?.permissions?.financials?.permissions) {
    const financialKeys = [];
    currentUser.permissions.financials.permissions.forEach(section => {
      (section?.permissions || []).forEach(perm => {
        financialKeys.push(perm.key);
        if (perm.options) {
          perm.options.forEach(opt => financialKeys.push(opt.key));
        }
      });
    });
    if (financialKeys.includes(key)) return true;
  }

  if (currentUser?.permissions?.authorized_states?.Data?.states) {
    const stateKeys = currentUser.permissions.authorized_states.Data.states.map(
      s => `state_${s.state_code}`
    );
    if (stateKeys.includes(key)) return true;
  }

  if (currentUser?.permissions?.letters) {
    const lettersKeys = [];
    
    if (currentUser.permissions.letters.admin_permissions) {
      currentUser.permissions.letters.admin_permissions.forEach(p => lettersKeys.push(p.key));
    }
    
    if (currentUser.permissions.letters.user_permissions) {
      currentUser.permissions.letters.user_permissions.forEach(perm => {
        lettersKeys.push(perm.key);
        if (perm.options) {
          perm.options.forEach(opt => lettersKeys.push(opt.key));
        }
      });
    }
    
    if (lettersKeys.includes(key)) return true;
  }

  if (currentUser?.permissions?.review_system?.permissions) {
    const reviewKeys = currentUser.permissions.review_system.permissions.map(p => p.key);
    if (reviewKeys.includes(key)) return true;
  }

  return false;
};

export const buildCreateUserPayload = (formValues = {}, currentUser = null, templates = []) => {
  const templateIdSet = new Set((templates || []).map(t => t.id));
  const authorizedStates = ADJUSTER_LICENSE_STATES || [];
  const licensesData = extractLicenseData(formValues, authorizedStates);

  const user = {
    first_name: formValues.first_name ?? '',
    last_name: formValues.last_name ?? '',
    email: formValues.email ?? '',
    user_name: formValues.username ?? formValues.user_name ?? '',
    job_title: formValues.job_title ?? '',
    phone: formValues.phone ?? '',
    org_id: toOrgId(currentUser?.orgId),
    user_role: formValues.role ?? ROLES.UNSURE,
    job_role: Array.isArray(formValues.job_role) ? formValues.job_role.join(',') : (formValues.job_role ?? ''),
    supervisor_id: formValues.supervisor_id ? parseInt(formValues.supervisor_id, 10) : null,
    is_temp_user: formValues.temp_user === true || formValues.temp_user === 'true',
    coordinate: '0:160:92:252',
    is_email_verified: true,
    timezone: 'America/New_York',
  };

  if (formValues.middle_name) {
    user.middle_name = formValues.middle_name;
  }
  if (formValues.ee_code) {
    user.ee_code = formValues.ee_code;
  }

  const permissions = {};

  const dynamicPermissions = extractDynamicPermissions(formValues, currentUser);
  Object.assign(permissions, dynamicPermissions);

  Object.keys(formValues).forEach(key => {
    if (typeof formValues[key] === 'boolean') {
      if (key.startsWith('tmpl_')) {
        return;
      }

      if (isDynamicPermissionKey(key, currentUser)) {
        return;
      }

      permissions[key] = formValues[key];
    }
  });

  const licenses = licensesData.length > 0 ? { data: licensesData } : { data: [] };

  const selectedTemplates = (templates || [])
    .filter(template => {
      const templateKey = buildTemplateKey(template.module, template.id);
      return formValues[templateKey] === true;
    })
    .map(template => ({
      template_id: template.id,
      short_name: template.short_name || '',
      name: template.name || '',
      module: template.module ? template.module.toLowerCase() : '',
    }));

  const templatesPayload = { data: selectedTemplates.length > 0 ? selectedTemplates : [{}] };

  return {
    user,
    permissions,
    licenses,
    templates: templatesPayload,
  };
};

export const buildUpdateUserPayload = (id, formValues = {}, currentUser = null, templates = []) => {
  const basePayload = buildCreateUserPayload(formValues, currentUser, templates);

  const user = {
    ...basePayload.user,
    id: parseInt(id, 10),
  };

  return {
    user,
    permissions: basePayload.permissions,
    licenses: basePayload.licenses,
    templates: basePayload.templates,
  };
};

export const filterUsers = (users = [], activeFilter = 'All', searchTerm = '') => {
  let filtered = [...users];

  switch (activeFilter) {
    case 'Active':
      filtered = filtered.filter((u) => u.isActive && !u.isTempUser && !u.isBlocked);
      break;
    case 'Temporary':
      filtered = filtered.filter((u) => u.isTempUser);
      break;
    case 'Blocked':
      filtered = filtered.filter((u) => u.isBlocked);
      break;
    case 'All':
    default:
      break;
  }

  if (searchTerm && searchTerm.trim()) {
    const term = searchTerm.toLowerCase().trim();
    filtered = filtered.filter((u) => {
      const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
      return (
        fullName.includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.username.toLowerCase().includes(term) ||
        u.title.toLowerCase().includes(term)
      );
    });
  }

  return filtered;
};

const AVATAR_COLORS = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ff9800', '#ff5722', '#795548',
];

export const getInitials = (firstName = '', lastName = '') =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

export const getAvatarColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const DEFAULT_WORKKARD_ITEM = {
  id: null,
  workkardNumber: '',
  sponserName: '',
  sponserId: null,
  sponserProfileImage: null,
  assignedOpenActivities: 0,
  ownedOpenActivities: 0,
  assignedCloseActivities: 0,
  ownedCloseActivities: 0,
  status: '',
  rowId: null,
};

const DEFAULT_ACTIVITY_ITEM = {
  id: null,
  activityTitle: '',
  workkardId: null,
  workkardNumber: '',
  ownerId: null,
  ownerName: '',
  ownerProfileImage: null,
  assigneeId: null,
  assigneeName: '',
  assigneeProfileImage: null,
  resolution: '',
  progressPercentage: 0,
  dueDate: '',
  localDueDate: '',
  statusColorClass: '',
  rowId: null,
};

export const normalizeWorkkardItem = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_WORKKARD_ITEM };

  return {
    id: raw.WorkkardId ?? DEFAULT_WORKKARD_ITEM.id,
    workkardNumber: raw.WorkkardNumber ?? DEFAULT_WORKKARD_ITEM.workkardNumber,
    sponserName: raw.WorkkardSponserName ?? DEFAULT_WORKKARD_ITEM.sponserName,
    sponserId: raw.WorkkardSponserId ?? DEFAULT_WORKKARD_ITEM.sponserId,
    sponserProfileImage: raw.SponserProfileFullImage ?? raw.SponserProfileFullImageUrl ?? DEFAULT_WORKKARD_ITEM.sponserProfileImage,
    assignedOpenActivities: raw.AssignedOpenActivities ?? DEFAULT_WORKKARD_ITEM.assignedOpenActivities,
    ownedOpenActivities: raw.OwnedOpenActivities ?? DEFAULT_WORKKARD_ITEM.ownedOpenActivities,
    assignedCloseActivities: raw.AssignedCloseActivities ?? DEFAULT_WORKKARD_ITEM.assignedCloseActivities,
    ownedCloseActivities: raw.OwnedCloseActivities ?? DEFAULT_WORKKARD_ITEM.ownedCloseActivities,
    status: raw.Status ?? DEFAULT_WORKKARD_ITEM.status,
    rowId: raw.RowId ?? DEFAULT_WORKKARD_ITEM.rowId,
  };
};

export const normalizeActivityItem = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ACTIVITY_ITEM };

  return {
    id: raw.ActivityId ?? DEFAULT_ACTIVITY_ITEM.id,
    activityTitle: raw.ActivityTitle ?? DEFAULT_ACTIVITY_ITEM.activityTitle,
    workkardId: raw.WorkkardId ?? DEFAULT_ACTIVITY_ITEM.workkardId,
    workkardNumber: raw.WorkkardNumber ?? DEFAULT_ACTIVITY_ITEM.workkardNumber,
    ownerId: raw.OwnerId ?? DEFAULT_ACTIVITY_ITEM.ownerId,
    ownerName: raw.OwnerName ?? DEFAULT_ACTIVITY_ITEM.ownerName,
    ownerProfileImage: raw.OwnerProfileFullImage ?? raw.OwnerProfileFullImageUrl ?? DEFAULT_ACTIVITY_ITEM.ownerProfileImage,
    assigneeId: raw.AssigneeId ?? DEFAULT_ACTIVITY_ITEM.assigneeId,
    assigneeName: raw.AssigneeName ?? DEFAULT_ACTIVITY_ITEM.assigneeName,
    assigneeProfileImage: raw.AssigneeProfileFullImage ?? raw.AssigneeProfileImageFullUrl ?? DEFAULT_ACTIVITY_ITEM.assigneeProfileImage,
    resolution: raw.Resolution ?? DEFAULT_ACTIVITY_ITEM.resolution,
    progressPercentage: raw.ProgressPercentage ?? DEFAULT_ACTIVITY_ITEM.progressPercentage,
    dueDate: raw.DueDate ?? DEFAULT_ACTIVITY_ITEM.dueDate,
    localDueDate: raw.LocalDueDate ?? DEFAULT_ACTIVITY_ITEM.localDueDate,
    statusColorClass: raw.StatusColorClass ?? DEFAULT_ACTIVITY_ITEM.statusColorClass,
    rowId: raw.RowId ?? raw.rowId ?? DEFAULT_ACTIVITY_ITEM.rowId,
  };
};

export const normalizeWorkloadWorkkardsResponse = (response = {}) => {
  const rawData = response?.workloadData ?? response?.data ?? [];
  const items = Array.isArray(rawData) ? rawData.map(normalizeWorkkardItem) : [];
  const totalCount = response?.totalCount ?? items.length;

  return { items, totalCount };
};

export const normalizeWorkloadActivitiesResponse = (response = {}) => {
  const rawData = response?.workloadData ?? response?.data ?? [];
  const items = Array.isArray(rawData) ? rawData.map(normalizeActivityItem) : [];
  const totalCount = response?.totalCount ?? items.length;

  return { items, totalCount };
};